import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import React, { useEffect, useState } from "react";
import {
  type AdhocPreviewBatchQuery,
  useAdhocPreviewBatchQuery,
} from "../../generated/graphql";
import {
  BILLING_PARTY_MEANING,
  DEADLINE_TYPE_MEANING,
  meaningFor,
  SERVICE_MEANING,
} from "./adhoc-decision-panel";
import AdhocPreviewResult from "./adhoc-preview-result";

const POLL_INTERVAL_MS = 2000;

/**
 * Cap total continuous polling at ~10 minutes. A batch that has not settled by
 * then is almost certainly stuck (a strand or a DE outage); rather than poll
 * forever we pause and let the operator explicitly resume, so a forgotten tab
 * does not hammer the API indefinitely.
 */
const MAX_POLL_MS = 10 * 60 * 1000;

type BatchMember =
  AdhocPreviewBatchQuery["adhocPreviewBatch"]["members"][number];

const styles = {
  paper: { p: 2 },
  statusRow: { p: 2 },
  batchId: { fontFamily: "monospace" },
  mono: { fontFamily: "monospace", fontSize: 12 },
  tableWrap: { overflowX: "auto" },
  drillIn: { p: 2, bgcolor: "grey.50" },
  fileCell: { maxWidth: 220, wordBreak: "break-word" },
  // The drill-in cell collapses to zero padding; when closed it also drops its
  // bottom border so a collapsed row reads as a single line. Two stable objects
  // (rather than an inline `sx`) keep the cell from re-allocating its style on
  // every render.
  collapseCellOpen: { py: 0 },
  collapseCellClosed: { py: 0, border: 0 },
} satisfies Record<string, SxProps<Theme>>;

/** DE / batch lifecycle states that will not change again. */
const isTerminalStatus = (status: string): boolean =>
  status === "COMPLETED" || status === "FAILED";

const statusColor = (
  status: string | null | undefined,
): "success" | "error" | "info" | "warning" | "default" => {
  if (status === "COMPLETED") return "success";
  if (status === "FAILED") return "error";
  if (status === "DISPATCHED" || status === "RUNNING" || status === "PENDING")
    return "info";
  return "default";
};

/** The effective per-member status shown in the grid (dispatch failure wins). */
const memberStatusLabel = (member: BatchMember): string => {
  if (!isNil(member.dispatchError)) return "DISPATCH FAILED";
  return member.runStatus ?? "PENDING";
};

/** A monospace value cell, or an em-dash placeholder when nullish. */
const Mono = ({ value }: { readonly value: string | null | undefined }) =>
  isNil(value) ? (
    <Typography variant="body2" color="text.disabled">
      —
    </Typography>
  ) : (
    <Typography sx={styles.mono}>{value}</Typography>
  );

/** A mechanism chip glossed (via tooltip) from the shared decision vocabulary. */
const MechanismCell = ({
  mechanism,
  meanings,
}: {
  readonly mechanism: string | null | undefined;
  readonly meanings: Record<string, string>;
}) => {
  if (isNil(mechanism)) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const meaning = meaningFor(meanings, mechanism);
  const chip = <Chip size="small" label={mechanism} sx={styles.mono} />;
  return isNil(meaning) ? chip : <Tooltip title={meaning}>{chip}</Tooltip>;
};

/**
 * The per-field audit grid: one row per document, columns for the extracted
 * decision the V3 apply WOULD make — billing-party / service / deadline
 * mechanisms (glossed from the same vocabulary the single-doc decision panel
 * uses) plus the duplicate verdict. Each row expands into the EXISTING
 * single-doc viewer ({@link AdhocPreviewResult}) for the member's run, so the
 * drill-in is the same document + decision + exemplars + cost surface, not a
 * fork.
 */
const MemberRow = ({ member }: { readonly member: BatchMember }) => {
  const [open, setOpen] = useState(false);
  const plan = member.applyPlan;
  const canDrillIn = !isNil(member.adhocRunId);
  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            disabled={!canDrillIn}
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => {
              setOpen((prior) => !prior);
            }}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={styles.fileCell}>
          <Typography variant="body2">{member.fileName}</Typography>
        </TableCell>
        <TableCell>
          <Stack spacing={0.5}>
            <Chip
              size="small"
              label={memberStatusLabel(member)}
              color={
                isNil(member.dispatchError)
                  ? statusColor(member.runStatus)
                  : "error"
              }
            />
            {/* Surface the member's failure/read error inline, so a stuck or
                failed document is legible without expanding the row. A
                dispatch-failed member cannot drill in (its expander is
                disabled — there is no run), so its error MUST show here; a
                dispatched member surfaces any transient read error instead. */}
            {!isNil(member.dispatchError) && (
              <Typography variant="caption" color="error">
                {member.dispatchError}
              </Typography>
            )}
            {isNil(member.dispatchError) && !isNil(member.error) && (
              <Typography variant="caption" color="error">
                {member.error}
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <MechanismCell
            mechanism={plan?.billingParty.mechanism}
            meanings={BILLING_PARTY_MEANING}
          />
        </TableCell>
        <TableCell>
          <Mono value={plan?.billingParty.contactUuid} />
        </TableCell>
        <TableCell>
          <MechanismCell
            mechanism={plan?.service?.mechanism}
            meanings={SERVICE_MEANING}
          />
        </TableCell>
        <TableCell>
          <MechanismCell
            mechanism={plan?.deadlineType?.mechanism}
            meanings={DEADLINE_TYPE_MEANING}
          />
        </TableCell>
        <TableCell>
          {isNil(plan?.duplicate) ? (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          ) : (
            <Chip
              size="small"
              color={plan.duplicate.isDuplicate ? "warning" : "default"}
              label={plan.duplicate.isDuplicate ? "Duplicate" : "New"}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={8}
          sx={open ? styles.collapseCellOpen : styles.collapseCellClosed}
        >
          <Collapse unmountOnExit in={open} timeout="auto">
            {/* Only reachable when the member has a run (the expander is
                disabled otherwise), so this always renders the single-doc
                viewer. A dispatch failure has no run and surfaces its error in
                the status cell above instead. */}
            <Box sx={styles.drillIn}>
              {!isNil(member.adhocRunId) && (
                <AdhocPreviewResult runId={member.adhocRunId} />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/** The batch's aggregate token / cost line, or a placeholder when none seen. */
const AggregateLine = ({
  batch,
}: {
  readonly batch: AdhocPreviewBatchQuery["adhocPreviewBatch"];
}) => {
  const parts: string[] = [];
  if (!isNil(batch.aggregateInputTokens))
    parts.push(`${batch.aggregateInputTokens.toLocaleString()} in tok`);
  if (!isNil(batch.aggregateOutputTokens))
    parts.push(`${batch.aggregateOutputTokens.toLocaleString()} out tok`);
  if (!isNil(batch.aggregateCostUsd))
    parts.push(`$${batch.aggregateCostUsd.toFixed(4)}`);
  return (
    <Typography variant="body2" color="text.secondary">
      {parts.length > 0
        ? `Batch total · ${parts.join(" · ")}`
        : "Batch total · no usage recorded yet."}
    </Typography>
  );
};

/**
 * Polls `adhocPreviewBatch` for one triggered batch (~2s) until it reaches a
 * terminal status, then renders the batch header (status + aggregate cost), the
 * per-field audit grid, and per-document drill-ins. A non-terminal batch shows a
 * spinner beside the current status and keeps polling.
 *
 * @param props The component props.
 * @param props.batchId The preview batch UUID returned by the trigger.
 */
const AdhocPreviewBatchResult = ({ batchId }: { readonly batchId: string }) => {
  const { data, loading, error, startPolling, stopPolling, refetch } =
    useAdhocPreviewBatchQuery({
      variables: { id: batchId },
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
  const batch = data?.adhocPreviewBatch;

  // Each Resume press starts a fresh polling window (bumps the nonce, which
  // re-arms the expiry timer below and clears the paused flag).
  const [resumeNonce, setResumeNonce] = useState(0);
  const [pollingExpired, setPollingExpired] = useState(false);

  const nonTerminal = batch !== undefined && !isTerminalStatus(batch.status);
  const active = nonTerminal && !pollingExpired;

  useEffect(() => {
    if (active) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [active, startPolling, stopPolling]);

  // Bound total polling time. Keyed ONLY on [batchId, resumeNonce] so it clocks
  // one continuous ~10min window — NOT reset by every 2s poll's data change (a
  // dependency on `data`/`batch` would re-arm the timer forever). When it fires
  // the batch pauses; Resume bumps the nonce to start a new window.
  useEffect(() => {
    setPollingExpired(false);
    const timer = setTimeout(() => {
      setPollingExpired(true);
    }, MAX_POLL_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [batchId, resumeNonce]);

  const resumePolling = () => {
    setResumeNonce((prior) => prior + 1);
    void refetch();
  };

  if (batch === undefined) {
    if (error !== undefined) {
      return (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Failed to load batch: {error.message}
        </Alert>
      );
    }
    if (loading) {
      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={styles.statusRow}
        >
          <CircularProgress size={18} />
          <Typography>Loading batch…</Typography>
        </Stack>
      );
    }
    return <Typography sx={styles.statusRow}>Batch not found.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {/* A poll failure that arrives while stale data is still shown (errorPolicy
          'all' keeps the last good batch): surface it rather than silently
          leaving a spinner spinning on stale rows. */}
      {error !== undefined && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Failed to refresh the batch (showing the last known state):{" "}
          {error.message}
        </Alert>
      )}

      {/* Polling was paused after the time cap; the batch is still non-terminal.
          Let the operator resume an explicit fresh window. */}
      {nonTerminal && pollingExpired && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={resumePolling}>
              Resume polling
            </Button>
          }
        >
          Still running — polling paused after 10 minutes.
        </Alert>
      )}

      <Paper variant="outlined" sx={styles.paper}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="h6">Batch</Typography>
            <Chip
              size="small"
              label={batch.status}
              color={statusColor(batch.status)}
            />
            {active && <CircularProgress size={16} />}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={styles.batchId}
            >
              {batch.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {batch.memberCount} document
              {batch.memberCount === 1 ? "" : "s"} · company {batch.companyUuid}
            </Typography>
          </Stack>
          <AggregateLine batch={batch} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={styles.paper}>
        <Typography gutterBottom variant="h6">
          Per-document decision grid
        </Typography>
        <Box sx={styles.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Document</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Billing party</TableCell>
                <TableCell>Contact uuid</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell>Duplicate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batch.members.map((member) => (
                <MemberRow key={member.position} member={member} />
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Stack>
  );
};

export default AdhocPreviewBatchResult;
